/* eslint-disable react-refresh/only-export-components */
// src/components/ui/Toast.jsx - Global toast notification system
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import PortalIcon from "./PortalIcon";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const success = useCallback((msg, duration) => addToast(msg, "success", duration), [addToast]);
  const error = useCallback((msg, duration) => addToast(msg, "error", duration), [addToast]);
  const warning = useCallback((msg, duration) => addToast(msg, "warning", duration), [addToast]);
  const info = useCallback((msg, duration) => addToast(msg, "info", duration), [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, removeToast }) {
  return (
    <>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateY(10px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media(max-width:640px) {
          .toast-stack {
            left: 12px !important;
            right: 12px !important;
            bottom: 14px !important;
            width: auto !important;
          }
        }
      `}</style>
      <div className="toast-stack" style={{
        position: "fixed",
        bottom: "22px",
        right: "22px",
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "min(390px, calc(100vw - 24px))",
        pointerEvents: "none",
      }}>
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onRemove={() => removeToast(toast.id)} />
        ))}
      </div>
    </>
  );
}

function ToastItem({ toast, onRemove }) {
  const styles = {
    success: { accent:"#10B981", bg:"rgba(236,253,245,.98)", border:"rgba(16,185,129,.24)", icon:"check", title:"Success" },
    error: { accent:"#EF4444", bg:"rgba(254,242,242,.98)", border:"rgba(239,68,68,.24)", icon:"x", title:"Error" },
    warning: { accent:"#F59E0B", bg:"rgba(255,251,235,.98)", border:"rgba(245,158,11,.26)", icon:"info", title:"Warning" },
    info: { accent:"#6366F1", bg:"rgba(238,242,255,.98)", border:"rgba(99,102,241,.24)", icon:"info", title:"Info" },
  };

  const style = styles[toast.type] || styles.info;

  useEffect(() => {
    const timer = setTimeout(onRemove, 5000);
    return () => clearTimeout(timer);
  }, [onRemove]);

  return (
    <div
      onClick={onRemove}
      style={{
        background: style.bg,
        color: "#0F172A",
        padding: "12px",
        borderRadius: "14px",
        border: `1px solid ${style.border}`,
        fontWeight: 600,
        fontSize: ".86rem",
        boxShadow: "0 18px 42px rgba(15,23,42,.16)",
        animation: "toastSlideIn .22s ease",
        display: "flex",
        alignItems: "flex-start",
        gap: "11px",
        pointerEvents: "auto",
        cursor: "pointer",
        backdropFilter: "blur(10px)",
      }}
    >
      <span style={{ width:30, height:30, borderRadius:10, background:`${style.accent}16`, color:style.accent, display:"inline-flex", alignItems:"center", justifyContent:"center", flex:"0 0 auto" }}>
        <PortalIcon name={style.icon} size={16} stroke={2.4} />
      </span>
      <span style={{ flex: 1, minWidth:0 }}>
        <span style={{ display:"block", color:"#0F172A", fontSize:".8rem", fontWeight:900, marginBottom:2 }}>{style.title}</span>
        <span style={{ display:"block", color:"#475569", lineHeight:1.45, overflowWrap:"anywhere" }}>{toast.message}</span>
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label="Dismiss notification"
        style={{
          background: "none",
          border: "none",
          color: "#64748B",
          cursor: "pointer",
          padding: "4px",
          lineHeight: 0,
          borderRadius:8,
        }}
      >
        <PortalIcon name="x" size={15} />
      </button>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
